/**
 * Cuentas conectadas — UI de toggles e interruptor de freno por cuenta.
 *
 * Cada cuenta (IG/FB/WhatsApp del cliente) renderiza una card con tres toggles
 * (moderación, modo prueba, publicación automática) y un selector de nivel
 * de freno de emergencia. Cambios disparan PATCH + audit entry automático.
 */
import { useState } from "react";
import { useParams } from "@tanstack/react-router";
import { AlertTriangle, Info, Unplug, Plus, X } from "lucide-react";
import {
  useAccounts,
  useUpdateToggle,
  useSetKillSwitch,
  useDeleteAccount,
  useAvailableAccounts,
  useAddAccount,
  type AccountWithToggle,
  type KillSwitchLevel,
} from "@/hooks/use-accounts";
import { findClient } from "@/lib/clients";
import { EmptyState } from "@/components/shared/empty-state";
import { cn } from "@/lib/cn";
import type { ClientSlug, Platform } from "@/types";

const PLATFORM_LABEL: Record<string, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  whatsapp: "WhatsApp",
  tiktok: "TikTok",
  x: "X",
  youtube: "YouTube",
  linkedin: "LinkedIn",
  threads: "Threads",
};

const KILL_LEVELS: Array<{ value: KillSwitchLevel; label: string; description: string }> = [
  { value: "none", label: "Ninguno", description: "Bot operando normal" },
  {
    value: "bot_only",
    label: "Pausar bot",
    description: "El bot no responde, los humanos sí pueden",
  },
  {
    value: "bot_and_escalations",
    label: "Bot + escalaciones",
    description: "No se procesa nada nuevo, las escalaciones no se notifican",
  },
  { value: "full", label: "Apagado total", description: "Cuenta totalmente congelada" },
];

export function AccountsPage() {
  const { clientSlug } = useParams({ strict: false }) as { clientSlug?: string };
  const client = findClient(clientSlug);
  const { data: accounts, isLoading } = useAccounts(client?.slug);
  const [addOpen, setAddOpen] = useState(false);

  return (
    <div className="min-h-full bg-background overflow-y-auto">
      <div className="max-w-4xl mx-auto px-7 py-8">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-sans text-3xl font-semibold tracking-tight text-text-primary">
              Cuentas
            </h1>
            <p className="font-sans text-sm text-text-muted mt-1">
              {client
                ? `Cuentas conectadas a ${client.fullName}`
                : "Cuentas conectadas"}
            </p>
          </div>
          {client && (
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-magenta text-white font-sans text-sm font-medium hover:bg-magenta-hover transition-colors"
            >
              <Plus size={15} strokeWidth={2} />
              Agregar plataforma
            </button>
          )}
        </header>

        {client && addOpen && (
          <AddPlatformModal
            clientSlug={client.slug as ClientSlug}
            clientName={client.fullName}
            onClose={() => setAddOpen(false)}
          />
        )}

        {isLoading ? (
          <div className="text-sm text-text-muted">Cargando cuentas…</div>
        ) : !accounts?.length ? (
          <EmptyState
            title="Sin cuentas conectadas"
            description="Cuando una cuenta de Instagram, Facebook o WhatsApp se conecte vía Zernio, aparece acá con sus controles."
          />
        ) : (
          <div className="space-y-4">
            {accounts.map((acc) => (
              <AccountCard key={acc.id} account={acc} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AddPlatformModal({
  clientSlug,
  clientName,
  onClose,
}: {
  clientSlug: ClientSlug;
  clientName: string;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useAvailableAccounts(clientSlug);
  const add = useAddAccount();
  const items = data?.items ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 py-16 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="card-surface w-full max-w-lg p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-1">
          <h2 className="font-sans text-lg font-semibold text-text-primary">
            Agregar plataforma
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text-primary transition-colors"
            title="Cerrar"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>
        <p className="font-sans text-sm text-text-muted mb-5">
          Plataformas conectadas en Zernio para {clientName} que todavía no están
          en el CM. Al agregarlas quedan con la moderación y publicación
          apagadas — las prendés a mano.
        </p>

        {isLoading ? (
          <div className="text-sm text-text-muted py-6 text-center">
            Buscando en Zernio…
          </div>
        ) : isError ? (
          <div className="text-sm text-error-text py-6 text-center">
            No se pudo consultar Zernio. Reintentá en un momento.
          </div>
        ) : data?.reason === "sin_profile" ? (
          <div className="text-sm text-text-muted py-6 text-center">
            Este cliente todavía no tiene ninguna cuenta cargada, así que no
            podemos derivar su perfil de Zernio. Cargá la primera cuenta desde
            el panel de administración.
          </div>
        ) : items.length === 0 ? (
          <div className="text-sm text-text-muted py-6 text-center">
            No hay plataformas nuevas para agregar. Todo lo conectado en Zernio
            ya está en el CM.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <div
                key={item.zernioAccountId}
                className="flex items-center justify-between gap-3 p-3 rounded-lg border border-default bg-surface-1"
              >
                <div className="min-w-0">
                  <div className="font-sans text-sm font-medium text-text-primary">
                    {item.label}
                  </div>
                  <div className="font-sans text-xs text-text-muted truncate">
                    {item.displayName ?? "—"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    add.mutate({
                      clientSlug,
                      zernioAccountId: item.zernioAccountId,
                      label: item.label,
                    })
                  }
                  disabled={add.isPending}
                  className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-magenta text-white font-sans text-xs font-medium hover:bg-magenta-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus size={13} strokeWidth={2} />
                  Agregar
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AccountCard({ account }: { account: AccountWithToggle }) {
  const toggle = account.toggle;
  const moderationOn = toggle?.moderationEnabled ?? false;
  const dryRun = toggle?.dryRun ?? false;
  const publishingOn = toggle?.publishingEnabled ?? false;
  const killLevel = (toggle?.killSwitchLevel ?? "none") as KillSwitchLevel;

  const updateToggle = useUpdateToggle();
  const setKill = useSetKillSwitch();

  // Estado de confirmación pendiente para los toggles. Cualquier intento de
  // flipear pide confirmación inline antes de mandar el PATCH — evita
  // apagar/encender por accidente.
  const [pendingConfirm, setPendingConfirm] = useState<
    null | { field: "moderationEnabled" | "dryRun" | "publishingEnabled"; next: boolean }
  >(null);

  function requestFlip(
    field: "moderationEnabled" | "dryRun" | "publishingEnabled",
  ) {
    const next =
      field === "moderationEnabled"
        ? !moderationOn
        : field === "dryRun"
          ? !dryRun
          : !publishingOn;
    setPendingConfirm({ field, next });
  }

  function confirmFlip() {
    if (!pendingConfirm) return;
    updateToggle.mutate(
      {
        clientSlug: account.clientSlug as ClientSlug,
        platform: account.platform as Platform,
        [pendingConfirm.field]: pendingConfirm.next,
      },
      {
        onSettled: () => setPendingConfirm(null),
      },
    );
  }

  const killActive = killLevel !== "none";

  return (
    <div
      className={cn(
        "card-surface p-5",
        killActive && "border-l-[3px]",
      )}
      style={killActive ? { borderLeftColor: "var(--error)" } : undefined}
    >
      {/* Header de la cuenta */}
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="min-w-0">
          <h3 className="font-sans text-lg font-semibold text-text-primary">
            {PLATFORM_LABEL[account.platform] ?? account.platform}
          </h3>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="font-sans text-sm text-text-secondary">
              {account.handle ? `@${account.handle}` : "—"}
            </span>
            <span className="text-text-disabled">·</span>
            <span className="font-sans text-xs text-text-muted">
              Token: {account.tokenMode === "champe" ? "Champe" : "Cliente"}
            </span>
            <span className="text-text-disabled">·</span>
            <span className="font-mono text-2xs text-text-muted" title="Zernio account ID">
              {account.zernioAccountId.slice(0, 12)}…
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {killActive && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-2xs font-sans font-semibold bg-error-bg text-error-text border border-error-border">
              <AlertTriangle size={11} strokeWidth={2} />
              Freno activo
            </span>
          )}
          <DisconnectButton accountId={account.id} platformLabel={PLATFORM_LABEL[account.platform] ?? account.platform} handle={account.handle ?? null} />
        </div>
      </div>

      {/* Toggles */}
      <div className="space-y-3 mb-5">
        <ToggleRow
          label="Moderación automática"
          description="El bot responde por sí solo a mensajes y comentarios entrantes"
          value={moderationOn}
          onChange={() => requestFlip("moderationEnabled")}
          disabled={updateToggle.isPending || !!pendingConfirm}
          confirm={
            pendingConfirm?.field === "moderationEnabled"
              ? {
                  next: pendingConfirm.next,
                  pending: updateToggle.isPending,
                  onConfirm: confirmFlip,
                  onCancel: () => setPendingConfirm(null),
                }
              : undefined
          }
        />
        <ToggleRow
          label="Modo prueba (dry-run)"
          description="El bot genera respuestas pero NO las envía a la red. Útil para revisar qué hubiera dicho."
          value={dryRun}
          onChange={() => requestFlip("dryRun")}
          disabled={updateToggle.isPending || !!pendingConfirm}
          confirm={
            pendingConfirm?.field === "dryRun"
              ? {
                  next: pendingConfirm.next,
                  pending: updateToggle.isPending,
                  onConfirm: confirmFlip,
                  onCancel: () => setPendingConfirm(null),
                }
              : undefined
          }
        />
        <ToggleRow
          label="Publicación automática"
          description="Sección de publicaciones todavía no activa en cm.champe — el panel está enfocado en moderación."
          value={publishingOn}
          onChange={() => {}}
          disabled
          forceDisabled
        />
      </div>

      {/* Freno de emergencia */}
      <div className="pt-4 border-t border-subtle">
        <div className="flex items-center gap-2 mb-3">
          <h4 className="font-sans text-sm font-semibold text-text-primary">
            Freno de emergencia
          </h4>
          <span className="font-mono text-2xs uppercase tracking-wider text-text-muted">
            requiere razón
          </span>
        </div>
        <KillSwitchControl
          currentLevel={killLevel}
          isPending={setKill.isPending}
          onApply={(level, reason) =>
            setKill.mutate({
              clientSlug: account.clientSlug as ClientSlug,
              platform: account.platform as Platform,
              level,
              reason,
            })
          }
        />
      </div>
    </div>
  );
}

function DisconnectButton({
  accountId,
  platformLabel,
  handle,
}: {
  accountId: number;
  platformLabel: string;
  handle: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const del = useDeleteAccount();

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Desconectar cuenta"
        className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-default text-text-muted hover:text-error-text hover:bg-error-bg hover:border-error-border transition-colors font-sans text-2xs font-medium"
      >
        <Unplug size={12} strokeWidth={1.75} />
        Desconectar
      </button>
      {open && (
        <div className="absolute z-30 right-0 top-full mt-2 card-surface p-4 w-80 shadow-lg">
          <h4 className="font-sans text-sm font-semibold text-text-primary mb-1">
            Desconectar {platformLabel}
            {handle ? ` @${handle}` : ""}
          </h4>
          <p className="font-sans text-xs text-text-muted mb-3">
            Sale del listado y deja de recibir mensajes nuevos. Los mensajes
            históricos y el audit log se preservan. Podés reconectar después
            usando el mismo Zernio account ID.
          </p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Razón (queda en bitácora)"
            className="w-full px-3 py-2 text-sm font-sans rounded-md bg-surface-2 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none resize-none mb-3"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                if (!reason.trim() || del.isPending) return;
                del.mutate(
                  { accountId, reason: reason.trim() },
                  {
                    onSuccess: () => {
                      setOpen(false);
                      setReason("");
                    },
                  },
                );
              }}
              disabled={!reason.trim() || del.isPending}
              className="h-8 px-3 rounded-md bg-error-bg border border-error-border text-error-text font-sans text-xs font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {del.isPending ? "Desconectando…" : "Confirmar"}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setReason("");
              }}
              className="h-8 px-3 rounded-md text-xs font-sans text-text-muted hover:text-text-primary transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface ToggleConfirm {
  next: boolean;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function ToggleRow({
  label,
  description,
  value,
  onChange,
  disabled,
  forceDisabled,
  confirm,
}: {
  label: string;
  description: string;
  value: boolean;
  onChange: () => void;
  /** Pending por mutación en curso o por confirmación abierta. */
  disabled?: boolean;
  /** Desactivado por scope (no se puede habilitar). */
  forceDisabled?: boolean;
  /** Si está presente, este row está esperando confirmación del usuario. */
  confirm?: ToggleConfirm;
}) {
  const blocked = disabled || forceDisabled;
  // Cuando hay confirmación pendiente para ESTE row, mostramos el toggle en
  // su valor actual (no flippeamos hasta confirmar) y debajo la barra de
  // "¿Estás seguro?".
  const showConfirm = !!confirm;
  return (
    <div className={cn(forceDisabled && "opacity-60")}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="font-sans text-sm font-medium text-text-primary flex items-center gap-2">
            {label}
            {forceDisabled && (
              <span className="font-mono text-2xs uppercase tracking-wider text-text-disabled border border-default rounded-full px-1.5 py-0.5">
                próximamente
              </span>
            )}
          </div>
          <p className="font-sans text-xs text-text-muted mt-0.5">
            {description}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={forceDisabled ? false : value}
          aria-disabled={forceDisabled ? "true" : undefined}
          disabled={blocked && !showConfirm}
          onClick={blocked && !showConfirm ? undefined : onChange}
          title={forceDisabled ? "Sección no activa todavía" : undefined}
          className={cn(
            "relative inline-flex shrink-0 h-6 w-11 items-center rounded-full transition-colors",
            "focus:outline-none focus:shadow-focus",
            forceDisabled ? "bg-surface-3" : value ? "bg-magenta" : "bg-surface-3",
            blocked && !showConfirm && "opacity-50 cursor-not-allowed",
          )}
        >
          <span
            className={cn(
              "inline-block w-4 h-4 rounded-full bg-white transition-transform",
              forceDisabled
                ? "translate-x-1"
                : value
                  ? "translate-x-6"
                  : "translate-x-1",
            )}
          />
        </button>
      </div>

      {showConfirm && (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-magenta bg-state-human-bg px-3 py-2">
          <p className="font-sans text-xs text-text-primary">
            {confirm.next ? "Activar" : "Desactivar"}{" "}
            <span className="font-medium">{label.toLowerCase()}</span>
            {" — "}
            <span className="text-text-secondary">¿confirmás?</span>
          </p>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={confirm.onCancel}
              disabled={confirm.pending}
              className="h-7 px-2.5 rounded-md text-2xs font-sans text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={confirm.onConfirm}
              disabled={confirm.pending}
              className={cn(
                "h-7 px-3 rounded-md text-2xs font-sans font-medium inline-flex items-center gap-1",
                "bg-magenta text-white hover:bg-magenta-hover transition-colors",
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              {confirm.pending
                ? "Aplicando…"
                : confirm.next
                  ? "Sí, activar"
                  : "Sí, desactivar"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function KillSwitchControl({
  currentLevel,
  isPending,
  onApply,
}: {
  currentLevel: KillSwitchLevel;
  isPending: boolean;
  onApply: (level: KillSwitchLevel, reason: string) => void;
}) {
  const [selected, setSelected] = useState<KillSwitchLevel>(currentLevel);
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const changed = selected !== currentLevel;
  const requiresReason = selected !== "none";

  function handleApply() {
    if (selected === "full" && !confirming) {
      setConfirming(true);
      return;
    }
    onApply(selected, reason || "(sin razón)");
    setConfirming(false);
    setReason("");
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {KILL_LEVELS.map((opt) => {
          const active = selected === opt.value;
          const isCurrent = currentLevel === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => setSelected(opt.value)}
              className={cn(
                "text-left p-3 rounded-lg border transition-colors",
                "hover:bg-surface-2",
                active
                  ? "border-magenta bg-state-human-bg"
                  : "border-default bg-surface-1",
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-sans text-sm font-medium text-text-primary">
                  {opt.label}
                </span>
                {isCurrent && (
                  <span className="font-mono text-2xs uppercase tracking-wider text-text-muted">
                    actual
                  </span>
                )}
              </div>
              <p className="font-sans text-xs text-text-muted mt-0.5">
                {opt.description}
              </p>
            </button>
          );
        })}
      </div>

      {requiresReason && changed && (
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Razón del freno (queda en bitácora)"
          className="w-full h-9 px-3 text-sm font-sans rounded-md bg-surface-2 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none"
        />
      )}

      {changed && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleApply}
            disabled={isPending || (requiresReason && !reason)}
            className={cn(
              "h-9 px-3.5 rounded-lg text-sm font-medium font-sans inline-flex items-center gap-2",
              selected === "full"
                ? "bg-error-bg border border-error-border text-error-text hover:opacity-90"
                : "bg-magenta text-white hover:bg-magenta-hover",
              "disabled:opacity-50 disabled:cursor-not-allowed transition-colors",
            )}
          >
            {confirming
              ? "Confirmar apagado total"
              : isPending
                ? "Aplicando…"
                : "Aplicar"}
          </button>
          <button
            type="button"
            onClick={() => {
              setSelected(currentLevel);
              setReason("");
              setConfirming(false);
            }}
            className="h-9 px-3 rounded-lg text-sm font-sans text-text-muted hover:text-text-primary transition-colors"
          >
            Cancelar
          </button>
          {confirming && (
            <span className="font-sans text-xs text-error-text inline-flex items-center gap-1.5">
              <Info size={12} strokeWidth={1.75} />
              Apagado total congela todo. Hacé click otra vez para confirmar.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
